import { useMemo, useState } from "react";
import { format, isSameDay } from 'date-fns';
import heLocale from 'date-fns/locale/he';
import MyDateTime from "../DTechComp/MyDateTime";
import FutureDatePick from "./FutureDatePick";
import FutureTimeList from "./FutureTimeList";

// בורר תאריך+שעה להזמנה עתידית, מוצג inline בתוך אותו frame של בחירת סוג ההזמנה.
// בנוי באותה תבנית בדיוק כמו TblOrderReservation.js (הזמנת שולחן): שורת טאבים
// ("תאריך"/"שעה") מעל פאנל משותף יחיד שמתחלף לפי הטאב הפעיל - בלי פופאפים צפים.
// props: rest, master - לחישוב slots תקינים
// channel - "Deliver" | "Take"
// selectedSlot - ה-slot הנבחר כרגע (נשמר אצל ההורה)
// onSelect(dateObj) - קרוי כשנבחר slot
// onConfirm() - קרוי בלחיצה על "אישור" בשלב הסיכום (השלב האחרון)
const FutureOrderPicker = (props) => {

    //אם כבר קיים slot נבחר (למשל בפתיחת "החלף מועד" על הזמנה קיימת) -
    //מתחילים מהתאריך שלו ומהפאנל סגור (מציג את הסיכום), לא מ"היום"
    //ומטאב "תאריך" פתוח כברירת מחדל
    const [selectedDate, setSelectedDate] = useState(props.selectedSlot || new Date());
    const [activeTab, setActiveTab] = useState(props.selectedSlot ? null : "date"); // "date" | "time" | null

    const slots = useMemo(() =>
        MyDateTime.generateFutureSlots(props.rest, props.master, selectedDate, props.channel),
        [props.rest, props.master, props.channel, selectedDate]);

    const selectedSlot = props.selectedSlot;

    //בחירת תאריך רק מסמנת אותו, בדיוק כמו בחירת שעה (onTimeSelected) - לא
    //מתקדמת לטאב הבא. ההתקדמות נעשית אך ורק ע"י כפתור "אישור" החיצוני
    //(onMainConfirm), אותו רעיון עקבי בכל שלבי הבורר
    const onDateSelected = (date) => {
        //אם זה אותו יום קלנדרי כמו הבחירה הקודמת (למשל בעריכת הזמנה קיימת
        //ו"שינוי מועד" חוזר לאותו תאריך) - לא מנקים slot שכבר נבחר, אחרת
        //רשימת השעות נפתחת בלי שום סימון גם אם השעה עדיין תקפה לאותו יום
        if (!isSameDay(date, selectedDate)) {
            props.onSelect(null);
        }
        setSelectedDate(date);
    }

    //מסמן את השעה שנבחרה בצהוב - לא סוגר את הטאב ולא קופץ לסיכום. ההתקדמות
    //בפועל נעשית ע"י המשתמש בלחיצה על כפתור "אישור" החיצוני (ב-OrderTypeSelect /
    //ChangeScheduledForPopup), שנהיה לחיץ ברגע ש-selectedSlot מוגדר
    const onTimeSelected = (slot) => {
        props.onSelect(slot);
    }

    //כפתור "שינוי מועד" בסיכום - חוזר לעריכה, מתחילים שוב מבחירת תאריך
    const onChangeDateTime = () => {
        setActiveTab("date");
    }

    //הסיכום מוצג רק אחרי שגם תאריך וגם שעה נבחרו וסגרנו את שני הטאבים
    const showSummary = selectedSlot && !activeTab;

    //כפתור "אישור" יחיד שמלווה את שלושת השלבים ברצף: תאריך -> שעה -> סיכום.
    //בכל שלב הוא רק מתקדם לשלב הבא (או, בסיכום, קורא ל-onConfirm של ההורה
    //שמבצע בפועל את השינוי וסוגר את הפופאפ) - בלי לדלג שלבים
    const mainConfirmEnabled = activeTab === "date" ? !!selectedDate
        : activeTab === "time" ? !!selectedSlot
            : showSummary; // בסיכום - תמיד לחיץ
    const onMainConfirm = () => {
        if (activeTab === "date") {
            if (!selectedDate) return;
            setActiveTab("time");
        } else if (activeTab === "time") {
            if (!selectedSlot) return;
            setActiveTab(null);
        } else if (showSummary) {
            props.onConfirm();
        }
    }

    const dateTabLabel = isSameDay(selectedDate, new Date())
        ? "היום"
        : format(selectedDate, 'dd/MM/yyyy', { locale: heLocale });

    const timeTabLabel = selectedSlot ? MyDateTime.getShortTime(selectedSlot) : "שעה";

    //טווח השעות המוצג בסיכום - זהה לחישוב ב-OrderFunctions.futureArrivalRangeText,
    //רק שכאן צריך את שני הקצוות בנפרד לתצוגה בשתי שורות שונות
    const prepMinutes = props.channel === "Deliver" ? props.rest.Delivery?.Time : props.rest.TakeAway?.Time;
    const arrivalRange = useMemo(() => {
        if (!selectedSlot) return { startTxt: "", endTxt: "" };
        const endDate = new Date(selectedSlot.getTime() + (parseInt(prepMinutes) || 0) * 60 * 1000);
        return {
            startTxt: MyDateTime.getShortTime(selectedSlot),
            endTxt: MyDateTime.getShortTime(endDate)
        };
    }, [selectedSlot, prepMinutes]);

    return (
        <div className={"futureOrderPickerWrap"}>
            <div className={"futurePickerTitle bold centerText"}>
                {props.channel === "Deliver" ? "הזמנה עתידית של משלוח" : "הזמנה עתידית של איסוף"}
            </div>

            {!showSummary &&
                <div className={"futureTabsRow"}>
                    <div className={"futureTabWrap"}>
                        <button type="button"
                            className={`btnReset futureTabBtn ${activeTab === "date" ? "active" : ""}`}
                            onClick={() => setActiveTab(activeTab === "date" ? null : "date")}>
                            {dateTabLabel}
                        </button>
                    </div>
                    <div className={"futureTabWrap"}>
                        <button type="button"
                            className={`btnReset futureTabBtn ${activeTab === "time" ? "active" : ""}`}
                            onClick={() => setActiveTab(activeTab === "time" ? null : "time")}>
                            {timeTabLabel}
                        </button>
                    </div>
                </div>
            }

            <div className={"futurePickerPanel"}>
                {activeTab === "date" &&
                    <FutureDatePick
                        rest={props.rest}
                        master={props.master}
                        channel={props.channel}
                        selectedDate={selectedDate}
                        onSelect={(date) => onDateSelected(date)}
                    />
                }
                {activeTab === "time" &&
                    <FutureTimeList
                        slots={slots}
                        selectedSlot={selectedSlot}
                        onSelect={(slot) => onTimeSelected(slot)}
                    />
                }
            </div>

            {showSummary &&
                <div className={"futureSummaryBox centerText"}>
                    <div className={"futureSummaryLine"}>
                        לתאריך <span className={"bold"}>{format(selectedSlot, 'dd/MM/yyyy', { locale: heLocale })}</span>
                    </div>
                    <div className={"futureSummaryLine"}>
                        בין השעות <span className={"bold"}>{arrivalRange.startTxt}-{arrivalRange.endTxt}</span>
                    </div>
                    <div className={"futureSummaryLine futureSummaryWarn"}>
                        * חשוב לציין שייתכנו עיכובים קלים או חריגה במספר דקות למועד המבוקש, אנחנו כמובן עושים
                        את כל המאמצים לעמוד בזמנים אבל תמיד ייתכנו תקלות לא מתוכננות.
                    </div>
                    <button type="button" className={"btnReset futureChangeDateBtn"}
                        onClick={() => onChangeDateTime()}>
                        שינוי מועד
                    </button>
                </div>
            }

            <button type="button" className={"btnReset selectOpBtn futureContinueBtn"}
                disabled={!mainConfirmEnabled}
                onClick={() => onMainConfirm()}>
                אישור
            </button>
        </div>
    );
}

export default FutureOrderPicker;
